/**
 * Deck card component for deck list view
 */
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import CardImage from '@/components/CardImage/CardImage';
import ColorDots from '@/components/ColorDots/ColorDots';
import { DECK_FORMAT_LABELS, DECK_STATUS_LABELS } from '@/lib/deckUtils';
import type { Color } from '@/types/card';
import type { Deck, DeckStatus } from '@/types/deck';

export interface DeckCardProps {
  deck: Deck;
  coverImageUris?: { small?: string; normal?: string; large?: string } | null;
  colorIdentity?: Color[];
  variant?: 'default' | 'compact';
}

const statusBadgeStyles: Record<DeckStatus, string> = {
  brewing: 'bg-amber-500/20 text-amber-400 ring-amber-500/30',
  active: 'bg-green-500/20 text-green-400 ring-green-500/30',
  archived: 'bg-slate-500/20 text-slate-400 ring-slate-500/30',
};

export default function DeckCard({ deck, coverImageUris = null, colorIdentity, variant = 'default' }: DeckCardProps): ReactElement {
  const isCompact = variant === 'compact';
  const hasDescription = deck.description && deck.description.trim().length > 0;

  if (isCompact) {
    return (
      <Link
        to={`/decks/${deck.id}`}
        className="bg-storm/50 hover:bg-storm/80 ring-ash/20 group hover:ring-teal/40 relative flex h-20 overflow-hidden rounded-xl ring-1 transition-all"
      >
        {/* Cover image */}
        <div className="relative w-14 shrink-0 overflow-hidden">
          {coverImageUris ? (
            <CardImage
              imageUris={coverImageUris}
              name={deck.name}
              size="small"
              showPlaceholder={false}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="bg-slate/50 flex h-full w-full items-center justify-center">
              <svg className="text-ash h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
          )}
        </div>
        {/* Content */}
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-3 py-2">
          <h3 className="text-pearl group-hover:text-teal truncate text-sm font-semibold">{deck.name}</h3>
          <div className="text-ash flex items-center gap-2 text-xs">
            {deck.format && <span className="bg-void/50 rounded px-1 py-0.5 text-xs">{DECK_FORMAT_LABELS[deck.format]}</span>}
            <span>{deck.card_count} cards</span>
            {colorIdentity && colorIdentity.length > 0 && <ColorDots colors={colorIdentity} />}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/decks/${deck.id}`}
      className="bg-storm/50 hover:bg-storm/80 ring-ash/20 group hover:ring-teal/40 relative flex h-40 overflow-hidden rounded-xl ring-1 transition-all"
    >
      {/* Cover image - flush to edge with gradient overlay */}
      <div className="relative w-28 shrink-0 overflow-hidden">
        {coverImageUris ? (
          <CardImage
            imageUris={coverImageUris}
            name={deck.name}
            size="small"
            showPlaceholder={false}
            className="h-full w-full object-cover object-left"
          />
        ) : (
          <div className="bg-slate/50 flex h-full w-full items-center justify-center">
            <svg className="text-ash h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
        )}
        {/* Gradient overlay for smooth edge */}
        <div className="from-storm/80 pointer-events-none absolute inset-y-0 right-0 w-6 bg-linear-to-r to-transparent" />
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col px-4 py-3">
        {/* Top: Deck name + description */}
        <div>
          <h3 className="text-pearl group-hover:text-teal truncate text-base font-semibold">{deck.name}</h3>
          {hasDescription && <p className="text-ash/70 mt-1 line-clamp-2 text-xs leading-relaxed">{deck.description}</p>}
        </div>

        {/* Middle: Card count + format + colors - centered vertically */}
        <div className="flex-1 py-2">
          <div className="text-ash flex items-center gap-2 text-xs">
            <span>
              {deck.card_count} {deck.card_count === 1 ? 'card' : 'cards'}
            </span>
            {deck.format && <span className="bg-void/50 rounded px-1.5 py-0.5">{DECK_FORMAT_LABELS[deck.format]}</span>}
            {colorIdentity && colorIdentity.length > 0 && <ColorDots colors={colorIdentity} />}
          </div>
        </div>

        {/* Bottom: Updated date + status badge */}
        <div className="flex items-center justify-between">
          <p className="text-ash/70 text-xs">Updated {formatDistanceToNow(deck.updated_at, { addSuffix: true })}</p>
          <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium ring-1', statusBadgeStyles[deck.status])}>
            {DECK_STATUS_LABELS[deck.status]}
          </span>
        </div>
      </div>
    </Link>
  );
}
